import React, { useContext, useEffect, useState } from 'react';
import get from 'lodash/get';
import styled, { keyframes, css, withTheme } from 'styled-components';
import { assets, getMediaSrc } from '@one/theme-utils';

import usePrevious from '../util/usePrevious';

import Link from '../util/Link';
import { NavigationContext, TOGGLE_MOBILE_DRAWER } from '../context/NavigationContext';
import { getPathToRoute } from '../util/getPathToRoute';
import { createPageViewRequest } from '../util/createPageViewRequest';
import getCookie from '../util/getCookie';
import getStreamateAutologinLink from '../requests/loadStreamateAutologinLink';

const defaultStyles = {};

const openKeyframes = keyframes`
    from { transform: translateX(0); margin-right: 0; }
    to { transform: translateX(50%); margin-right: 100%; }
    `;
const closedKeyframes = keyframes`
    from { transform: translateX(50%); margin-right: 100%; }
    to { transform: translateX(0); margin-right: 0%; }
    `;

const getAnimation = ({ menuOpen, structureType }) => (menuOpen !== null && structureType !== 'desktop'
    ? css`
              animation: ${menuOpen ? openKeyframes : closedKeyframes} 0.3s forwards linear;
          `
    : css`
              animation: none;
          `);

const InitComponent = styled(({ menuOpen, structureType, ...rest }) => <Link {...rest} />)`
    position: relative;
    display: flex;
    align-items: center;
    min-width: 0;
    max-width: 100%;
    height: 100%;
    flex-shrink: 0;
    flex-basis: auto;
    transform: translateX(0);
    z-index: 2;
    padding-right: 16px;
    @media only screen and (min-width: 480px) {
        ${getAnimation}
    }
    img {
        max-height: 100%;
        max-width: 225px;
        width: 100%;
    }
`;

const StyledComponent = styled(InitComponent)(({ styles }) => styles);

const Logo = ({
    styles = {},
    attributes,
    client: { hasWebPSupport = true } = {},
    structureType,
    ...rest
}) => {
    const {
        path = '', // custom attribute path, '' if not set
        shouldOpenNewTab = false,
        shouldForceRefresh = false,
        popunder = { selected: false },
        isNoFollow = false,
        customPath = '',
        enableRedirect = true, // for compatibility with an old settings without new variable
    } = attributes;

    const {
        dispatch,
        navigationConfig,
        onFlirt4FreeLink,
        state: { menuOpen },
        routesMap,
        pageType,
        pageId,
        statePage,
        instanceId,
        user: { memberUuid } = {},
        navigationApiUrl,
    } = useContext(NavigationContext);

    const componentStyles = {
        ...defaultStyles,
        ...styles,
    };

    const isMenuOpen = usePrevious(menuOpen) === undefined ? null : menuOpen;
    const [streamateAutologinLink, setStreamateAutologinLink] = useState('/');
    const logoPath = get(navigationConfig, `${structureType}.path`, 'home');
    const altLabel = get(attributes, 'alt', '');

    let logoUrl = getPathToRoute({ routesMap, path: { type: 'HOME' } });
    if (path !== '') {
        if (path === 'streamateAutologin') {
            logoUrl = streamateAutologinLink;
        } else {
            logoUrl = getPathToRoute({ routesMap, path, customPath });
        }
    } else if (logoPath !== 'home') {
        logoUrl = get(navigationConfig, `${structureType}.path`, { type: 'HOME' });
    }

    useEffect(() => {
        if (path === 'streamateAutologin') {
            const sessionId = getCookie('__s');
            const userToken = getCookie('access_token_ma');

            getStreamateAutologinLink({ navigationApiUrl, memberUuid, sessionId, userToken })
                .then(response => {
                    setStreamateAutologinLink(response.streamateAutoLoginUrl);
                })
                .catch(() => {
                    setStreamateAutologinLink('/');
                });
        }
    }, [memberUuid]);

    const handleLogoClick = urlForRefresh => () => {
        createPageViewRequest({
            eventName: 'HEADER_LOGO',
            origin: pageType,
            instanceId,
            pageId,
            statePage,
            parentEvent: '',
            currentUrl: window.location.href
        });

        if (path === 'flirt4Free' && typeof onFlirt4FreeLink === 'function') {
            onFlirt4FreeLink({ shouldOpenNewTab });
        } else {
            if (isMenuOpen) {
                dispatch({ type: TOGGLE_MOBILE_DRAWER });
            }
            if (shouldForceRefresh) {
                window.location.replace(urlForRefresh);
            }
        }
    };

    // forces default logo image
    let imgSrc;
    if (attributes && attributes.image === 'default') {
        imgSrc = assets('general.logo', rest);
    } else {
        imgSrc = attributes.src;
    }

    return React.useMemo(
        () => (
            <StyledComponent
                {...rest}
                to={logoUrl}
                disabled={!enableRedirect}
                onClick={handleLogoClick(logoUrl)}
                isOnlyClickActionComponent={path === 'flirt4Free'}
                menuOpen={isMenuOpen}
                popunder={popunder}
                styles={componentStyles}
                {...(path !== '' && shouldOpenNewTab && { target: '_blank' })}
                {...(path !== '' && isNoFollow && { rel: 'nofollow' })}
            >
                <img
                    src={getMediaSrc(
                        imgSrc,
                        hasWebPSupport,
                    )}
                    alt={altLabel}
                />
            </StyledComponent>
        ),
        [isMenuOpen, logoUrl, structureType, enableRedirect, streamateAutologinLink],
    );
};

export default withTheme(Logo);
